import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  MenuItem,
  Select,
  TextField,
  Typography,
  withStyles,
} from "@material-ui/core";
import { useSnackbar } from "material-ui-snackbar-provider";
import { BarcodeScan } from "mdi-material-ui";
import React, { Suspense, useCallback, useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import AsyncButton from "../../../../components/AsyncButton";
import BarcodeScanner from "../../../../components/BarcodeScanner";
import useLocalStorageState from "../../../../util/useLocalStorageState";
import { resetPassword } from "../../api";

const QrCodeReader = React.lazy(() =>
  import("../../../../components/QrCodeReader")
);

const styles = (theme) => ({
  employee: {
    width: 250,
    margin: `${theme.spacing.unit * 3}px auto 0`,
    display: "block",
  },
  alternative: {
    display: "flex",
  },
  qrScanner: {
    height: 400,
    marginTop: theme.spacing.unit * 2,
  },
  barcodeScanner: {
    fontSize: "48px",
    margin: `${theme.spacing.unit * 3}px auto 0`,
    display: "block",
  },
  flex: {
    flex: 1,
  },
  cameraLoading: {
    marginTop: theme.spacing.unit * 2,
    background: "#000",
    height: 400,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: theme.shape.borderRadius,
    "& > *": {
      color: "#fff",
    },
  },
  loading: {
    margin: "0 auto",
  },
  selectRoot: {
    marginLeft: theme.spacing.unit,
  },
  select: {
    paddingLeft: theme.spacing.unit,
    fontSize: "0.875rem",
  },
  dialogActionsReverse: {
    flexDirection: "row-reverse",
  },
});

function UserForm({
  classes,
  onSubmit,
  loading,
  authenticationError,
  invite,
  username: initialUsername,
}) {
  const { t } = useTranslation();
  const [username, setUsername] = React.useState(initialUsername ?? "");
  const [password, setPassword] = React.useState("");
  const [mode, setMode] = useLocalStorageState(
    "skl-user-login-mode",
    "credentials"
  );

  useEffect(() => {
    setUsername(initialUsername ?? "");
  }, [initialUsername]);

  const handleSetMode = useCallback(
    (e) => {
      setMode(e.target.value);
    },
    [setMode]
  );

  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  const codeProcessing = useRef(false);
  const handleLoginCode = useCallback(async (content) => {
    if (
      codeProcessing.current ||
      !(content.startsWith("ey") || content.startsWith("L-"))
    )
      return;
    codeProcessing.current = true;
    try {
      await onSubmitRef.current(
        content.startsWith("ey")
          ? { accessToken: content }
          : { barcode: content }
      );
    } finally {
      codeProcessing.current = false;
    }
  }, []);

  const isResettingPassword =
    authenticationError === "PASSWORD_RESET_REQUIRED" ||
    authenticationError === "PASSWORD_RESET";

  const [newPassword, setNewPassword] = React.useState("");
  const [repeatNewPassword, setRepeatNewPassword] = React.useState("");
  const [passwordResetError, setPasswordResetError] = React.useState(false);

  const snackbar = useSnackbar();

  const handleResetPassword = useCallback(async () => {
    setPasswordResetError(false);
    if (newPassword === "") {
      return;
    }
    if (newPassword !== repeatNewPassword) {
      setPasswordResetError("NOT_REPEATED");
      return;
    }
    try {
      if (invite) {
        await resetPassword({ inviteToken: invite.inviteToken, newPassword });
        snackbar.showMessage(t("Das Password wurde erfolgreich festgelegt."));
        onSubmitRef.current({
          username: invite.user.name,
          password: newPassword,
        });
      } else {
        await resetPassword({ username, password, newPassword });
        snackbar.showMessage(t("Das Password wurde erfolgreich geändert."));
        onSubmitRef.current({ username, password: newPassword });
      }
      setPasswordResetError(null);
    } catch (e) {
      setPasswordResetError(e);
    }
  }, [newPassword, repeatNewPassword, username, password, invite, snackbar, t]);

  return invite || isResettingPassword ? (
    <>
      <DialogContent>
        <DialogContentText color="textPrimary" variant="body2">
          {invite ? (
            <Trans>
              Willkommen beim StoreKeeper Smart. Bitte legen Sie Ihr Passwort
              fest und merken Sie sich Ihren Benutzernamen. Sie benötigen diese
              Informationen, um sich anzumelden.
            </Trans>
          ) : authenticationError === "PASSWORD_RESET_REQUIRED" ? (
            <Trans>
              Bitte vergeben Sie ein neues Passwort, um den StoreKeeper Smart
              weiterhin zu benutzen.
            </Trans>
          ) : null}
        </DialogContentText>
        {invite ? (
          <TextField
            className={classes.employee}
            label={t("Benutzername")}
            margin="dense"
            variant="filled"
            value={invite?.user.name ?? username}
            disabled
          />
        ) : authenticationError === "PASSWORD_RESET" ? (
          <TextField
            className={classes.employee}
            label={t("Altes Passwort")}
            margin="dense"
            variant="filled"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={passwordResetError?.statusCode === 401}
            helperText={
              passwordResetError?.statusCode === 401
                ? t("Falsches Passwort")
                : undefined
            }
            InputLabelProps={{ shrink: true }}
            autoFocus
          />
        ) : (
          <input name="username" value={username} type="hidden" />
        )}
        <TextField
          className={classes.employee}
          label={t("Neues Passwort")}
          margin="dense"
          variant="filled"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          error={
            !!passwordResetError &&
            passwordResetError?.statusCode !== 401 &&
            passwordResetError !== "NOT_REPEATED"
          }
          helperText={
            passwordResetError?.statusCode !== 401 &&
            passwordResetError?.code === "PASSWORD_DECLINED"
              ? t(passwordResetError.details)
              : undefined
          }
          InputLabelProps={{ shrink: true }}
          autoComplete="new-password"
          autoFocus={authenticationError !== "PASSWORD_RESET"}
        />
        <TextField
          className={classes.employee}
          label={t("Neues Passwort (wiederholen)")}
          margin="dense"
          variant="filled"
          type="password"
          value={repeatNewPassword}
          onChange={(e) => setRepeatNewPassword(e.target.value)}
          error={!!passwordResetError && passwordResetError?.statusCode !== 401}
          helperText={
            passwordResetError === "NOT_REPEATED"
              ? t("Die beiden Passwörter stimmen nicht miteinander überein.")
              : undefined
          }
          InputLabelProps={{ shrink: true }}
          autoComplete="new-password"
          onKeyUp={(e) => {
            if (e.key === "Enter") {
              handleResetPassword({ username, password });
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <AsyncButton color="primary" onClick={handleResetPassword}>
          {t("Weiter", { context: "setup" })}
        </AsyncButton>
      </DialogActions>
    </>
  ) : (
    <>
      <DialogContent>
        <DialogContentText color="textPrimary" variant="body2">
          {mode === "credentials" ? (
            <Trans>
              Bitte geben Sie Ihre Zugangsdaten ein, um den StoreKeeper Smart zu
              benutzen.
            </Trans>
          ) : (
            <Trans>
              Bitte scannen Sie Ihren QR-Code oder Barcode, um den StoreKeeper
              Smart zu benutzen.
            </Trans>
          )}
        </DialogContentText>
        {mode === "credentials" && (
          <>
            <TextField
              className={classes.employee}
              label={t("Benutzername")}
              margin="dense"
              variant="filled"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              error={authenticationError}
              InputLabelProps={{ shrink: true }}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  onSubmit({ username, password });
                }
              }}
            />
            <TextField
              className={classes.employee}
              label={t("Passwort")}
              margin="dense"
              variant="filled"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={authenticationError}
              helperText={
                authenticationError ? t("Falsche Zugangsdaten") : undefined
              }
              InputLabelProps={{ shrink: true }}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  onSubmit({ username, password });
                }
              }}
            />
          </>
        )}
        {mode === "camera" && (
          <Suspense
            fallback={
              <div className={classes.cameraLoading}>
                <CircularProgress />
              </div>
            }
          >
            <QrCodeReader
              className={classes.qrScanner}
              onScan={handleLoginCode}
            />
          </Suspense>
        )}
        {mode === "scanner" && (
          <BarcodeScanner onScan={handleLoginCode}>
            <BarcodeScan className={classes.barcodeScanner} />
          </BarcodeScanner>
        )}
        {mode !== "credentials" && authenticationError && (
          <Typography color="error">
            {t("Der gescannte Code ist ungültig.")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActionsReverse}>
        <AsyncButton
          color="primary"
          onClick={() => onSubmit({ username, password })}
          disabled={
            username === "" ||
            password === "" ||
            loading ||
            mode !== "credentials"
          }
        >
          {t("Weiter", { context: "setup" })}
        </AsyncButton>
        <div className={classes.flex} />
        <Select
          value={mode}
          onChange={handleSetMode}
          disableUnderline
          classes={{ select: classes.select, root: classes.selectRoot }}
        >
          <MenuItem value="credentials">{t("Benutzername/Passwort")}</MenuItem>
          <MenuItem value="camera">{t("QR-Code (Kamera)")}</MenuItem>
          <MenuItem value="scanner">
            {t("QR-Code/Barcode (Barcode-Scanner)")}
          </MenuItem>
        </Select>
      </DialogActions>
    </>
  );
}

export default withStyles(styles)(UserForm);
